import React, {useEffect, useRef, useState} from 'react'
import {
  StudyGuideBox,
  CentralBox,
  RightBox,
  DescriptionBox,
  StyledLogo,
  StyledIcon,
  Description,
  TimeBox,
  DaysBox,
  DayRow,
  DayTitle,
  InProgress,
  CollapseIcon,
  DayTitleFlexBoxColumn,
  DayTitleFlexBoxWrap,
  DayContent,
  DayItem,
  DayText,
  DayItems,
  DayBullet,
  DayInnerDivider,
  FloatingIcon,
  SubTitle,
  DayItemTitle,
  DayItemContent,
  NavBox,
  ContentBox,
  ContentFlexBox,
  Download,
  Indicator,
  IndicatorBall,
  DayContentFlexBox,
  RightBoxContent,
  RightBoxOverflow,
} from './styles'
import Breadcrumb, {BreadcrumbType} from '../../components/Breadcrumb'
import GuidedStudyProcessLogo from '../../../images/icons/new-dashboard/proven-process-icon.svg'
import CompleteIcon from '../../../images/icons/new-dashboard/complete-icon.svg'
import ClockIcon from '../../../images/icons/new-dashboard/clock-icon.svg'
import ClockCompleteIcon from '../../../images/icons/new-dashboard/clock-complete-icon.svg'
import VideoIcon from '../../../images/icons/new-dashboard/video-icon.svg'
import QuizIcon from '../../../images/icons/new-dashboard/quiz-icon.svg'
import DownloadIcon from '../../../images/icons/new-dashboard/download-icon.svg'
import LectureSlidesIcon from '../../../images/icons/new-dashboard/slides-icon.svg'
import BreakIcon from '../../../images/icons/new-dashboard/break-icon.svg'
import ExamIcon from '../../../images/icons/new-dashboard/exam-icon.svg'
import FlashcardIcon from '../../../images/icons/new-dashboard/flashcard-icon.svg'
import TeaCupIcon from '../../../images/icons/new-dashboard/teacup-icon.svg'

import ExamCompleteIcon from '../../../images/icons/new-dashboard/exam-light-grey-icon.svg'
import TeaCupCompleteIcon from '../../../images/icons/new-dashboard/teacup-light-grey-icon.svg'
import FlashcardCompleteIcon from '../../../images/icons/new-dashboard/flashcard-light-grey-icon.svg'
import QuizCompleteIcon from '../../../images/icons/new-dashboard/quiz-light-grey-icon.svg'
import VideoCompleteIcon from '../../../images/icons/new-dashboard/video-light-grey-icon.svg'
import LectureSlidesCompleteIcon from '../../../images/icons/new-dashboard/slides-light-grey-icon.svg'

import FlashcardGreyIcon from '../../../images/icons/new-dashboard/flashcard-grey-icon.svg'
import QuizGreyIcon from '../../../images/icons/new-dashboard/quiz-grey-icon.svg'
import VideoGreyIcon from '../../../images/icons/new-dashboard/video-grey-icon.svg'
import LectureSlidesGreyIcon from '../../../images/icons/new-dashboard/slides-grey-icon.svg'

import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {getDivisionDetails} from '../../../constants/divisions'
import ProgressCard from '../../components/Cards/ProgressCard'
import CurrentStatusCard from '../../components/Cards/CurrentStatusCard'
import VirtualWorkshopCard from '../../components/Cards/VirtualWorkshopCard'
import {useWindowDimensions} from '../../../hooks/useWindowDimensions'
import {LinkBox} from '../../../styles/global'
import {Link as RouterLink, useLocation} from 'react-router-dom'
import Sidenav from '../../components/Sidenav'
import {
  selectStudyGuideStatus,
  StudyGuide,
  StudyGuideAssets,
} from '../../../redux/studyGuides'
import {useSelector} from 'react-redux'
import {LoadingStatuses} from '../../../components/types/LoadingStatusses'
import Loading from '../../../components/Loading'
import {
  formatDurationToHHMM,
  formatDurationToHHMMSS,
} from '../../../utils/formatDate'
import {UpgradeBox} from '../../../components/DashboardContent/styles'
import {selectMember, selectMemberAccess} from '../../../redux/members'
import {useAppDispatch} from '../../../redux/configureStore'
import {useAuth} from '../../../components/AuthProvider'
import {Button} from '../../../components/button'
import {handleUpgrade} from '../../../utils/upgradeHelper'
import ChallengeCard from '../../components/Cards/ChallengeCard'
import DailyProgressIndicator from '../../../components/DailyProgressIndicator'
import {Attempt, selectNcarbAttempts} from '../../../redux/reportExamStatus'
import {
  TooltipBox,
  WhiteTooltip,
} from '../../components/DivisionProgress/styles'
import StudyGuideToast from '../../components/StudyGuideToast'
import {selectActiveChallenge} from '../../../redux/challenges'
import ReferFriendCard from '../../components/Cards/ReferFriendCard'
import StatsCard from '../../components/Cards/StatsCard'

type Props = {
  studyGuide?: StudyGuide
  onSaveProgress: (value: number, studyDay: number) => void
}

const View = ({studyGuide, onSaveProgress}: Props) => {
  const division = studyGuide?.abbreviation ?? null

  const auth = useAuth()
  const dispatch = useAppDispatch()
  const access = useSelector(selectMemberAccess)
  const member = useSelector(selectMember)
  const examReports = useSelector(selectNcarbAttempts)
  const challenge = useSelector(selectActiveChallenge)

  const hasSgAccess =
    access && Object.keys(access).length !== 0
      ? Number(access.are?.study_guides?.value) === 1
      : false

  const location = useLocation()

  const {isMobile} = useWindowDimensions()
  const studyGuideStatus = useSelector(selectStudyGuideStatus)

  const divisionDetails = getDivisionDetails(division)

  const breadcrumbs: BreadcrumbType[] = [
    {
      title: studyGuide?.courseTitle ?? '',
      url: location.pathname,
    },
  ]

  const [isAtTop, setIsAtTop] = useState(true)
  const [expandedItems, setExpandedItems] = useState<boolean[]>([])

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY <= 0)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (studyGuide?.assets) {
      setExpandedItems(
        studyGuide.assets.map(day =>
          hasSgAccess ? day.inProgress || false : false,
        ),
      )
    }
  }, [studyGuide?.assets])

  const contentRefs = useRef<(HTMLDivElement | null)[]>([])

  const toggleExpand = (index: number) => {
    setExpandedItems(prev =>
      prev.map((isExpanded, i) => (i === index ? !isExpanded : isExpanded)),
    )
  }

  const handleSaveProgress = (asset: StudyGuideAssets) => {
    if (asset.assetType === 'lecture_slides') {
      onSaveProgress(asset.assetGlobalId, asset.studyDay)
      window.open(asset.downloadUrl)
    }
  }

  const handleClickOnDay = (index: number, studyDay: number) => {
    toggleExpand(index)

    const element = document.getElementById(`study-day-${studyDay}`)
    if (element) {
      const offset = 130
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  const currentDay = studyGuide?.assets?.find(a => a.inProgress)

  const getExpectedDay = () => {
    const uniqueReports: Attempt[] = Object.values(
      (examReports ?? [])
        .filter(report => report.courseId)
        .reduce<Record<string, Attempt>>((acc, report) => {
          if (
            !acc[report.courseId] ||
            new Date(report.updatedAt) >
              new Date(acc[report.courseId].updatedAt)
          ) {
            acc[report.courseId] = report
          }
          return acc
        }, {}),
    )

    const normalize = (date: Date) =>
      new Date(date.getFullYear(), date.getMonth(), date.getDate())

    const report = uniqueReports.find(r => r.courseId === studyGuide?.courseId)

    if (studyGuide?.startedAt && report) {
      const startedAt = new Date(studyGuide.startedAt?.split('T')[0])
      startedAt.setHours(startedAt.getHours() + 12)

      const date = new Date(report.examDate)
      date.setHours(date.getHours() + 12)

      const today = new Date()

      const exam = normalize(date)
      const start = normalize(startedAt)
      const now = normalize(today)

      const msPerDay = 1000 * 60 * 60 * 24
      const totalDays = Math.max(
        Math.ceil((exam.getTime() - start.getTime()) / msPerDay),
        1,
      )
      const daysPassed = Math.max(
        Math.ceil((now.getTime() - start.getTime()) / msPerDay),
        0,
      )

      const totalGuideDays = studyGuide.timeWindow
      const expectedDay = Math.floor((daysPassed / totalDays) * totalGuideDays)

      return Math.min(expectedDay, totalGuideDays) === 0
        ? 1
        : Math.min(expectedDay, totalGuideDays)
    } else {
      return currentDay?.studyDay
    }
  }

  return (
    <StudyGuideBox>
      <NavBox>
        <Sidenav />
      </NavBox>
      <ContentBox>
        {studyGuideStatus === LoadingStatuses.Loading ||
        studyGuideStatus === LoadingStatuses.Idle ? (
          <Loading />
        ) : (
          <>
            {challenge?.hasEntry && <StudyGuideToast studyGuide={studyGuide} />}
            <Breadcrumb breadcrumbs={breadcrumbs} />
            <ContentFlexBox>
              <CentralBox>
                <DescriptionBox style={{background: '#fff'}}>
                  <SubTitle>
                    <StyledIcon src={divisionDetails?.icon} alt="division" />{' '}
                    {divisionDetails?.name}
                  </SubTitle>
                  <TimeBox>
                    <img src={ClockIcon} alt="course time" />{' '}
                    {formatDurationToHHMM(Number(studyGuide?.duration))}
                  </TimeBox>
                  <Description>{studyGuide?.description}</Description>
                  <UpgradeBox>
                    <StyledLogo
                      src={GuidedStudyProcessLogo}
                      alt="Guided Study Process"
                    />
                    {!hasSgAccess && (
                      <Button
                        color="secondary"
                        children={'Upgrade to Expert'}
                        onClick={handleUpgrade(
                          dispatch,
                          auth,
                          member,
                          'proven process paywall',
                        )}
                      />
                    )}
                  </UpgradeBox>
                </DescriptionBox>

                {isMobile && (
                  <RightBox>
                    <ProgressCard division={studyGuide?.abbreviation ?? null} />
                    <CurrentStatusCard
                      courseId={studyGuide?.courseId}
                      division={studyGuide?.abbreviation ?? ''}
                      expectedDay={getExpectedDay() ?? 1}
                      currentDay={currentDay?.studyDay ?? 1}
                    />
                    <VirtualWorkshopCard courseId={studyGuide?.courseId} />
                  </RightBox>
                )}

                <DaysBox>
                  <DailyProgressIndicator
                    studyGuide={studyGuide}
                    expectedDay={getExpectedDay() ?? 1}
                    onClickOnDay={handleClickOnDay}
                  />
                </DaysBox>

                <DaysBox>
                  {studyGuide?.assets?.map((asset, key) => {
                    return (
                      <DayRow
                        key={`day-${key}`}
                        id={asset.inProgress ? 'progress' : ''}
                        $isBreakDay={asset.isBreakDay}
                        $isExamDay={asset.isExamDay}
                        $isLastRow={
                          (studyGuide.assets ?? []).length === key + 1
                        }
                      >
                        <DayTitle
                          $isComplete={asset.isComplete}
                          onClick={() => toggleExpand(key)}
                          id={`study-day-${asset.studyDay}`}
                        >
                          <DayTitleFlexBoxColumn>
                            <DayTitleFlexBoxWrap>
                              {asset.isComplete && (
                                <FloatingIcon
                                  src={CompleteIcon}
                                  alt="Day completed"
                                />
                              )}
                              {`Day ${asset.studyDay}`}
                              {asset.isBreakDay ? (
                                <>
                                  <DayBullet>•</DayBullet>
                                  <span>
                                    <img
                                      src={
                                        asset.isComplete
                                          ? TeaCupCompleteIcon
                                          : TeaCupIcon
                                      }
                                    />{' '}
                                    Take a Break
                                  </span>
                                </>
                              ) : null}
                              {asset.isExamDay ? (
                                <>
                                  <DayBullet>•</DayBullet>
                                  <span>
                                    <img
                                      src={
                                        asset.isComplete
                                          ? ExamCompleteIcon
                                          : ExamIcon
                                      }
                                      alt="Exam"
                                    />
                                    {!isMobile ? `Practice Exam` : `Exam`}
                                  </span>
                                </>
                              ) : null}
                              {asset.duration ? (
                                <>
                                  <DayBullet>•</DayBullet>
                                  <TimeBox
                                    $isComplete={asset.isComplete}
                                    style={{paddingTop: 0}}
                                  >
                                    <img
                                      src={
                                        asset.isComplete
                                          ? ClockCompleteIcon
                                          : ClockIcon
                                      }
                                      alt="Assets duration"
                                    />{' '}
                                    {formatDurationToHHMM(asset.duration)}
                                  </TimeBox>
                                </>
                              ) : null}
                              {asset.inProgress && (
                                <InProgress>In progress</InProgress>
                              )}
                            </DayTitleFlexBoxWrap>
                            {(asset.hasFlashcard ||
                              asset.hasQuiz ||
                              asset.hasSlide ||
                              asset.hasVideo) && (
                              <DayContentFlexBox $isComplete={asset.isComplete}>
                                {asset.hasVideo && (
                                  <span>
                                    <img
                                      src={
                                        asset.isComplete
                                          ? VideoCompleteIcon
                                          : VideoGreyIcon
                                      }
                                      alt="Video"
                                    />{' '}
                                    Video
                                  </span>
                                )}
                                {asset.hasQuiz && (
                                  <span>
                                    <img
                                      src={
                                        asset.isComplete
                                          ? QuizCompleteIcon
                                          : QuizGreyIcon
                                      }
                                      alt="Quiz"
                                    />{' '}
                                    Quiz
                                  </span>
                                )}
                                {asset.hasFlashcard && (
                                  <span>
                                    <img
                                      src={
                                        asset.isComplete
                                          ? FlashcardCompleteIcon
                                          : FlashcardGreyIcon
                                      }
                                      alt="Flashcard"
                                    />{' '}
                                    Flashcard
                                  </span>
                                )}
                                {asset.hasSlide && (
                                  <span>
                                    <img
                                      src={
                                        asset.isComplete
                                          ? LectureSlidesCompleteIcon
                                          : LectureSlidesGreyIcon
                                      }
                                      alt="Lecture Slides"
                                    />{' '}
                                    Lecture Slides
                                  </span>
                                )}
                              </DayContentFlexBox>
                            )}
                          </DayTitleFlexBoxColumn>
                          <CollapseIcon>
                            {expandedItems[key] ? <RemoveIcon /> : <AddIcon />}
                          </CollapseIcon>
                        </DayTitle>
                        <DayContent
                          $isExpanded={expandedItems[key]}
                          height={contentRefs.current[key]?.scrollHeight}
                          ref={el => (contentRefs.current[key] = el)}
                        >
                          {hasSgAccess ? (
                            <>
                              {asset.break && asset.break.length > 0 && (
                                <>
                                  <DayText $isBold={true}>
                                    Break/Passive Review:
                                  </DayText>
                                  <DayItems>
                                    {asset.break.map((sgBreak, vBreak) => {
                                      return (
                                        <LinkBox
                                          component={RouterLink}
                                          key={`break-${vBreak}`}
                                          to={`/study-guide/${studyGuide.id}/${asset.studyDay}/breaks/${sgBreak.id}`}
                                        >
                                          <DayItem>
                                            <DayItemTitle>
                                              <img
                                                src={
                                                  sgBreak.isComplete
                                                    ? CompleteIcon
                                                    : BreakIcon
                                                }
                                                alt={
                                                  'Take a break or review your notes'
                                                }
                                              />
                                              <DayText>
                                                {sgBreak.assetTitle}
                                              </DayText>
                                            </DayItemTitle>
                                          </DayItem>
                                        </LinkBox>
                                      )
                                    })}
                                  </DayItems>
                                </>
                              )}
                              {asset.video && asset.video.length > 0 && (
                                <>
                                  <DayText $isBold={true}>
                                    Watch Videos:
                                  </DayText>
                                  <DayItems>
                                    {asset.video.map((video, vKey) => {
                                      return (
                                        <LinkBox
                                          component={RouterLink}
                                          key={`video-${vKey}`}
                                          to={`/study-guide/${studyGuide.id}/${asset.studyDay}/videos/${video.id}`}
                                        >
                                          <DayItem>
                                            <DayItemTitle>
                                              <img
                                                src={
                                                  video.isComplete
                                                    ? CompleteIcon
                                                    : VideoIcon
                                                }
                                                alt={video.assetTitle}
                                              />
                                              <DayText
                                                $isComplete={video.isComplete}
                                              >
                                                {video.assetTitle}
                                              </DayText>
                                            </DayItemTitle>
                                            <DayItemContent>
                                              {!isMobile && (
                                                <DayBullet>•</DayBullet>
                                              )}
                                              <DayText
                                                $isComplete={video.isComplete}
                                              >
                                                {formatDurationToHHMMSS(
                                                  Number(video.duration),
                                                )}
                                              </DayText>
                                            </DayItemContent>
                                          </DayItem>
                                        </LinkBox>
                                      )
                                    })}
                                  </DayItems>
                                </>
                              )}
                              {asset.quiz && asset.quiz.length > 0 && (
                                <>
                                  {asset.video && asset.video.length > 0 && (
                                    <DayInnerDivider />
                                  )}
                                  <DayText $isBold={true}>
                                    Complete Quiz:
                                  </DayText>
                                  <DayItems>
                                    {asset.quiz.map((quiz, qKey) => {
                                      return (
                                        <LinkBox
                                          component={RouterLink}
                                          key={`quiz-${qKey}`}
                                          to={`/study-guide/${studyGuide.id}/${asset.studyDay}/quizzes/${quiz.id}`}
                                        >
                                          <DayItem key={`quiz-${qKey}`}>
                                            <DayItemTitle>
                                              <img
                                                src={
                                                  quiz.isComplete
                                                    ? CompleteIcon
                                                    : QuizIcon
                                                }
                                                alt={quiz.assetTitle}
                                              />
                                              <DayText
                                                $isComplete={quiz.isComplete}
                                              >
                                                {quiz.assetTitle}
                                              </DayText>
                                            </DayItemTitle>
                                            <DayItemContent>
                                              {!isMobile && (
                                                <DayBullet>•</DayBullet>
                                              )}
                                              <DayText
                                                $isComplete={quiz.isComplete}
                                              >
                                                {formatDurationToHHMMSS(
                                                  Number(quiz.duration),
                                                )}
                                              </DayText>
                                            </DayItemContent>
                                            <DayItemContent>
                                              {!isMobile && (
                                                <DayBullet>•</DayBullet>
                                              )}
                                              <DayText
                                                $isComplete={quiz.isComplete}
                                              >
                                                {quiz.questions} questions
                                              </DayText>
                                            </DayItemContent>
                                          </DayItem>
                                        </LinkBox>
                                      )
                                    })}
                                  </DayItems>
                                </>
                              )}
                              {asset.flashcard_deck &&
                                asset.flashcard_deck.length > 0 && (
                                  <>
                                    {((asset.video && asset.video.length > 0) ||
                                      (asset.quiz &&
                                        asset.quiz.length > 0)) && (
                                      <DayInnerDivider />
                                    )}
                                    <DayText $isBold={true}>
                                      Flashcards:
                                    </DayText>
                                    <DayItems>
                                      {asset.flashcard_deck.map(
                                        (flashcard, fKey) => {
                                          return (
                                            <LinkBox
                                              component={RouterLink}
                                              key={`flashcard-${fKey}`}
                                              to={`/study-guide/${studyGuide.id}/${asset.studyDay}/flashcards/${flashcard.id}`}
                                            >
                                              <DayItem key={`quiz-${fKey}`}>
                                                <DayItemTitle>
                                                  <img
                                                    src={
                                                      flashcard.isComplete
                                                        ? CompleteIcon
                                                        : FlashcardIcon
                                                    }
                                                    alt={flashcard.assetTitle}
                                                  />
                                                  <DayText
                                                    $isComplete={
                                                      flashcard.isComplete
                                                    }
                                                  >
                                                    {flashcard.assetTitle}
                                                  </DayText>
                                                </DayItemTitle>
                                                <DayItemContent>
                                                  {!isMobile && (
                                                    <DayBullet>•</DayBullet>
                                                  )}
                                                  <DayText
                                                    $isComplete={
                                                      flashcard.isComplete
                                                    }
                                                  >
                                                    {formatDurationToHHMMSS(
                                                      Number(
                                                        flashcard.duration,
                                                      ),
                                                    )}
                                                  </DayText>
                                                </DayItemContent>
                                                <DayItemContent>
                                                  {!isMobile && (
                                                    <DayBullet>•</DayBullet>
                                                  )}
                                                  <DayText
                                                    $isComplete={
                                                      flashcard.isComplete
                                                    }
                                                  >
                                                    {flashcard.cards} cards
                                                  </DayText>
                                                </DayItemContent>
                                              </DayItem>
                                            </LinkBox>
                                          )
                                        },
                                      )}
                                    </DayItems>
                                  </>
                                )}
                              {asset.lecture_slides &&
                                asset.lecture_slides.length > 0 && (
                                  <>
                                    {((asset.video && asset.video.length > 0) ||
                                      (asset.quiz && asset.quiz.length > 0) ||
                                      (asset.flashcard_deck &&
                                        asset.flashcard_deck.length > 0)) && (
                                      <DayInnerDivider />
                                    )}
                                    <DayText $isBold={true}>
                                      Download & Review:
                                    </DayText>
                                    <DayItems>
                                      {asset.lecture_slides.map(
                                        (resource, vRes) => {
                                          return (
                                            <DayItem
                                              key={`resource-${vRes}`}
                                              onClick={() =>
                                                handleSaveProgress(resource)
                                              }
                                            >
                                              <DayItemTitle>
                                                <img
                                                  src={
                                                    resource.isComplete
                                                      ? CompleteIcon
                                                      : LectureSlidesIcon
                                                  }
                                                  alt={resource.assetTitle}
                                                />
                                                <DayText
                                                  $isComplete={
                                                    resource.isComplete
                                                  }
                                                >
                                                  {resource.assetTitle}
                                                </DayText>
                                              </DayItemTitle>

                                              <DayItemContent>
                                                {!isMobile && (
                                                  <DayBullet>•</DayBullet>
                                                )}
                                                <DayText
                                                  $isComplete={
                                                    resource.isComplete
                                                  }
                                                >
                                                  {formatDurationToHHMMSS(
                                                    Number(resource.duration),
                                                  )}
                                                </DayText>
                                              </DayItemContent>
                                              <DayItemContent>
                                                {!isMobile && (
                                                  <DayBullet>•</DayBullet>
                                                )}
                                                <Download>
                                                  Download Here
                                                </Download>
                                                <img
                                                  src={DownloadIcon}
                                                  alt="Download"
                                                />
                                              </DayItemContent>
                                            </DayItem>
                                          )
                                        },
                                      )}
                                    </DayItems>
                                  </>
                                )}
                              {asset.practice_exam &&
                                asset.practice_exam.length > 0 && (
                                  <>
                                    {((asset.video && asset.video.length > 0) ||
                                      (asset.quiz && asset.quiz.length > 0) ||
                                      (asset.flashcard_deck &&
                                        asset.flashcard_deck.length > 0) ||
                                      (asset.lecture_slides &&
                                        asset.lecture_slides.length > 0)) && (
                                      <DayInnerDivider />
                                    )}
                                    <DayText $isBold={true}>
                                      Practice Exam:
                                    </DayText>
                                    <DayItems>
                                      {asset.practice_exam.map(
                                        (exam, vExam) => {
                                          return (
                                            <LinkBox
                                              component={RouterLink}
                                              key={`exam-${vExam}`}
                                              to={`/study-guide/${studyGuide.id}/${asset.studyDay}/practice-exams/${exam.id}`}
                                            >
                                              <DayItem>
                                                <DayItemTitle>
                                                  <img
                                                    src={
                                                      exam.isComplete
                                                        ? CompleteIcon
                                                        : ExamIcon
                                                    }
                                                    alt={exam.assetTitle}
                                                  />
                                                  <DayText
                                                    $isComplete={
                                                      exam.isComplete
                                                    }
                                                  >
                                                    {exam.assetTitle}
                                                  </DayText>
                                                </DayItemTitle>
                                                <DayItemContent>
                                                  {!isMobile && (
                                                    <DayBullet>•</DayBullet>
                                                  )}
                                                  <DayText
                                                    $isComplete={
                                                      exam.isComplete
                                                    }
                                                  >
                                                    {formatDurationToHHMMSS(
                                                      Number(exam.duration),
                                                    )}
                                                  </DayText>
                                                </DayItemContent>
                                                <DayItemContent>
                                                  {!isMobile && (
                                                    <DayBullet>•</DayBullet>
                                                  )}
                                                  <DayText
                                                    $isComplete={
                                                      exam.isComplete
                                                    }
                                                  >
                                                    {exam.questions} questions
                                                  </DayText>
                                                </DayItemContent>
                                              </DayItem>
                                            </LinkBox>
                                          )
                                        },
                                      )}
                                    </DayItems>
                                  </>
                                )}
                            </>
                          ) : (
                            <>
                              You have reached our Guided Study Process.{' '}
                              <RouterLink
                                to={'#'}
                                onClick={handleUpgrade(
                                  dispatch,
                                  auth,
                                  member,
                                  'guided study process',
                                )}
                              >
                                Upgrade your membership
                              </RouterLink>{' '}
                              to access this material.
                            </>
                          )}
                        </DayContent>
                        {asset.studyDay === getExpectedDay() && (
                          <>
                            <WhiteTooltip
                              title={
                                <TooltipBox>
                                  <b>Target Tracker</b>
                                  <span>
                                    The Target Tracker helps you monitor your
                                    progress by showing where you should be
                                    based on your selected study plan, ensuring
                                    you stay on course and reach your goal on
                                    time.
                                  </span>
                                </TooltipBox>
                              }
                              arrow
                            >
                              <Indicator>
                                <IndicatorBall />
                              </Indicator>
                            </WhiteTooltip>
                          </>
                        )}
                      </DayRow>
                    )
                  })}
                </DaysBox>
              </CentralBox>
              {!isMobile && (
                <RightBox>
                  <RightBoxContent>
                    <RightBoxOverflow className={isAtTop ? 'top' : 'scrolled'}>
                      <ChallengeCard courseId={studyGuide?.courseId} />
                      <ProgressCard
                        division={studyGuide?.abbreviation ?? null}
                      />
                      <CurrentStatusCard
                        courseId={studyGuide?.courseId}
                        division={studyGuide?.abbreviation ?? ''}
                        expectedDay={getExpectedDay() ?? 1}
                        currentDay={currentDay?.studyDay ?? 1}
                      />
                      <VirtualWorkshopCard courseId={studyGuide?.courseId} />
                      <StatsCard studyGuide={studyGuide} />
                      <ReferFriendCard />
                    </RightBoxOverflow>
                  </RightBoxContent>
                </RightBox>
              )}
            </ContentFlexBox>
          </>
        )}
      </ContentBox>
    </StudyGuideBox>
  )
}

export default View
